/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CSSProperties } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';
import { Button, Text, UnstyledButton } from '@/shared/ui';
import { GearIcon } from '@/shared/ui/radix-icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/shadcn/dialog';
import { Switch } from '@/shared/ui/components/shadcn/switch';
import type { OverviewMode } from '@/services/housien/dashboard';
import { TILE_CATEGORIES, type TileCategory } from './Overview.catalogue';
import type { Tile } from './Overview.tile';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/** One card the dialog can offer: the card as built, and its section. */
export interface PickerTile {
  tile: Tile;
  category: TileCategory;
}

const ROW = 'flex items-center gap-(--space-3) rounded-(--radius-md) border border-(--border-subtle) bg-[var(--surface-primary)] px-(--space-3) py-(--space-2-5) transition-colors';

/**
 * What a row says about its card: accent, name, context line, and the
 * number it shows today -- picking a card is easier when you can see what
 * it would put on the screen.
 */
function RowIdentity({ tile }: { tile: Tile }) {
  return (
    <>
      <Text
        as="span"
        className="inline-block h-[16px] w-[3px] shrink-0 rounded-(--radius-full)"
        style={{ background: tile.accent }}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Text as="span" variant="bodySm" fw="var(--font-semibold)">
          {tile.label}
        </Text>
        <Text as="span" variant="meta" tone="tertiary" className="truncate">
          {tile.hint}
        </Text>
      </div>
      <Text as="span" variant="bodySm" fw="var(--font-semibold)" className="shrink-0 tabular-nums text-[var(--text-secondary)]">
        {tile.value}
      </Text>
    </>
  );
}

/**
 * A card that is on the screen: grip, identity, switch.
 *
 * The grip is its own element rather than the whole row, so dragging a row
 * never fights with the switch inside it.
 */
function ChosenRow({
  tile, canRemove, onToggle, t,
}: {
  tile: Tile;
  canRemove: boolean;
  onToggle: () => void;
  t: TFn;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: tile.id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      role="listitem"
      ref={setNodeRef}
      style={style}
      data-testid={`customize-chosen-${tile.id}`}
      className={`${ROW} hover:border-(--border-medium)`}
    >
      <UnstyledButton
        aria-label={t('dashboard.customize.reorder')}
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab touch-none rounded-(--radius-sm) p-(--space-0-5) text-[var(--text-tertiary)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text-secondary)] active:cursor-grabbing"
      >
        <DragHandleDots2Icon width={18} height={18} />
      </UnstyledButton>
      <RowIdentity tile={tile} />
      <Switch
        checked
        onChange={onToggle}
        disabled={!canRemove}
        size="sm"
        colorOn="bg-[var(--brand-accent)]"
        aria-label={t('dashboard.customize.remove', { name: tile.label })}
      />
    </div>
  );
}

/** A card that is not on the screen, offered to be. */
function AvailableRow({
  tile, canAdd, onToggle, t,
}: {
  tile: Tile;
  canAdd: boolean;
  onToggle: () => void;
  t: TFn;
}) {
  return (
    <div
      role="listitem"
      data-testid={`customize-available-${tile.id}`}
      className={`${ROW} ${canAdd ? 'hover:border-(--border-medium)' : 'opacity-60'}`}
    >
      <RowIdentity tile={tile} />
      <Switch
        checked={false}
        onChange={onToggle}
        disabled={!canAdd}
        size="sm"
        colorOn="bg-[var(--brand-accent)]"
        aria-label={t('dashboard.customize.add', { name: tile.label })}
      />
    </div>
  );
}

const SECTION_TITLE = 'text-(length:--text-body-sm) font-[var(--font-semibold)] text-[var(--text-primary)]';

/** The chosen cards of one section, in screen order. */
interface ChosenGroup {
  category: TileCategory;
  tiles: Tile[];
}

/**
 * Splits the chosen cards into the screen's fixed sections.
 *
 * The screen draws them that way, so the dialog does too: a list that
 * mixed sections would let a card be dragged to a place the screen never
 * puts it.
 */
function chosenGroups(chosen: Tile[], categoryOf: Map<string, TileCategory>): ChosenGroup[] {
  return TILE_CATEGORIES
    .map((category) => ({
      category,
      tiles: chosen.filter((tile) => categoryOf.get(tile.id) === category),
    }))
    .filter((group) => group.tiles.length > 0);
}

/**
 * Moves one card inside its own section and returns the whole list again,
 * section by section. A drop onto a card of another section is ignored:
 * sections do not move.
 */
function reorderWithin(groups: ChosenGroup[], activeId: string, overId: string): string[] | null {
  const group = groups.find((item) => item.tiles.some((tile) => tile.id === activeId));
  const ids = group?.tiles.map((tile) => tile.id) ?? [];
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (!group || to < 0) return null;
  const moved = arrayMove(ids, from, to);
  return groups.flatMap((item) => (item === group ? moved : item.tiles.map((tile) => tile.id)));
}

/** The cards on the screen, under their section, draggable within it. */
function ChosenSection({
  chosen, categoryOf, onReorder, onRemove, t,
}: {
  chosen: Tile[];
  categoryOf: Map<string, TileCategory>;
  onReorder: (ids: string[]) => void;
  onRemove: (id: string) => void;
  t: TFn;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const groups = chosenGroups(chosen, categoryOf);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const next = reorderWithin(groups, String(active.id), String(over.id));
    if (next) onReorder(next);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div data-testid="customize-chosen" className="flex flex-col gap-(--space-4)">
        {groups.map((group) => (
          <div key={group.category} className="flex flex-col gap-(--space-2)">
            <Text as="span" variant="meta" tone="secondary" fw="var(--font-semibold)">
              {t(`dashboard.groups.${group.category}`)}
            </Text>
            <SortableContext items={group.tiles.map((tile) => tile.id)} strategy={verticalListSortingStrategy}>
              <div role="list" className="flex flex-col gap-(--space-2)">
                {group.tiles.map((tile) => (
                  <ChosenRow
                    key={tile.id}
                    tile={tile}
                    // The last card stays: an empty list means "never chose"
                    // and would put the default set back, which is not what
                    // removing a card asks for.
                    canRemove={chosen.length > 1}
                    onToggle={() => onRemove(tile.id)}
                    t={t}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        ))}
      </div>
    </DndContext>
  );
}

/** The cards not on the screen, under the section they belong to. */
function AvailableSection({
  available, canAdd, onAdd, t,
}: {
  available: PickerTile[];
  canAdd: boolean;
  onAdd: (id: string) => void;
  t: TFn;
}) {
  const sections = TILE_CATEGORIES
    .map((category) => ({
      category,
      tiles: available.filter((item) => item.category === category).map((item) => item.tile),
    }))
    .filter((section) => section.tiles.length > 0);

  if (!sections.length) {
    return (
      <Text as="p" variant="meta" tone="tertiary">
        {t('dashboard.customize.nothingLeft')}
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-(--space-4)">
      {sections.map((section) => (
        <div key={section.category} className="flex flex-col gap-(--space-2)">
          <Text as="span" variant="meta" tone="secondary" fw="var(--font-semibold)">
            {t(`dashboard.groups.${section.category}`)}
          </Text>
          <div role="list" className="flex flex-col gap-(--space-2)">
            {section.tiles.map((tile) => (
              <AvailableRow
                key={tile.id}
                tile={tile}
                canAdd={canAdd}
                onToggle={() => onAdd(tile.id)}
                t={t}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The card picker for one view and one mode.
 *
 * Two lists: the cards on the screen, under their section and draggable within it, and
 * every other card the view can show, under its section. Changes land on
 * the board behind the dialog as they are made, so there is nothing to
 * confirm -- closing is done. At the mode's limit the offered cards lock
 * and the counter says why, rather than a switch that silently refuses.
 *
 * @param props.opened - Whether the dialog is open
 * @param props.onClose - Called when the dialog should close
 * @param props.mode - The mode being customised, for the copy and the limit
 * @param props.limit - How many cards this mode holds
 * @param props.catalogue - Every card the view can show, built
 * @param props.chosen - The ids on screen, in order
 * @param props.onChange - Called with the full new list of ids
 * @param props.onReset - Hands the mode back to its default set
 * @param props.saveFailed - The last change was refused and rolled back
 * @param props.t - Translation function
 */
export function CustomizeModal({
  opened, onClose, mode, limit, catalogue, chosen, onChange, onReset, saveFailed, t,
}: {
  opened: boolean;
  onClose: () => void;
  mode: OverviewMode;
  limit: number;
  catalogue: PickerTile[];
  chosen: string[];
  onChange: (ids: string[]) => void;
  onReset: () => void;
  saveFailed?: boolean;
  t: TFn;
}) {
  const byId = new Map(catalogue.map((item) => [item.tile.id, item.tile]));
  const categoryOf = new Map(catalogue.map((item) => [item.tile.id, item.category]));
  const chosenTiles = chosen.flatMap((id) => {
    const tile = byId.get(id);
    return tile ? [tile] : [];
  });
  const available = catalogue.filter((item) => !chosen.includes(item.tile.id));
  const atLimit = chosen.length >= limit;

  return (
    <Dialog open={opened} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        className="max-w-2xl gap-0 overflow-hidden rounded-(--radius-2xl) border-(--border-subtle) p-0"
        data-testid="overview-customize-modal"
      >
        {/* Same header grammar as the page it customises: seed-coloured icon,
            title on the app-title scale, mesh over the primary surface. */}
        <DialogHeader
          className="gap-(--space-1) space-y-0 border-b border-(--border-subtle) px-(--space-6) py-(--space-4) text-left"
          style={{ background: 'var(--mesh-gradient-light), var(--surface-primary)' }}
        >
          <div className="flex items-center gap-(--space-2)">
            <Text as="span" className="flex text-[var(--seed-base)]">
              <GearIcon width={18} height={18} />
            </Text>
            <DialogTitle className="text-[length:var(--text-app-title)] font-[var(--font-bold)] tracking-[var(--tracking-app-title)]">
              {t('dashboard.customize.title')}
            </DialogTitle>
          </div>
          <DialogDescription className="text-[length:var(--text-body-sm)] text-[var(--text-secondary)]">
            {t('dashboard.customize.body', { mode: t(`dashboard.mode.${mode}`), limit })}
          </DialogDescription>
        </DialogHeader>

        {/* Bounded here rather than on the dialog, so the lists are the only
            thing that scrolls and header and footer stay put. */}
        <div className="flex max-h-[min(60vh,32rem)] flex-col gap-(--space-5) overflow-y-auto px-(--space-6) py-(--space-4)">
          <div className="flex flex-col gap-(--space-2)">
            <div className="flex items-baseline justify-between gap-(--space-3)">
              <Text as="span" className={SECTION_TITLE}>
                {t('dashboard.customize.chosen')}
              </Text>
              <Text
                as="span"
                variant="meta"
                data-testid="customize-counter"
                className={atLimit ? 'text-[var(--status-warning-text)]' : 'text-[var(--text-tertiary)]'}
              >
                {t('dashboard.customize.counter', { count: chosen.length, limit })}
              </Text>
            </div>
            <ChosenSection
              chosen={chosenTiles}
              categoryOf={categoryOf}
              onReorder={onChange}
              onRemove={(id) => onChange(chosen.filter((item) => item !== id))}
              t={t}
            />
          </div>

          <div className="flex flex-col gap-(--space-2)">
            <Text as="span" className={SECTION_TITLE}>
              {t('dashboard.customize.available')}
            </Text>
            {atLimit && (
              <Text as="p" variant="meta" className="text-[var(--status-warning-text)]">
                {t('dashboard.customize.limitReached', { limit })}
              </Text>
            )}
            <AvailableSection
              available={available}
              canAdd={!atLimit}
              onAdd={(id) => onChange([...chosen, id])}
              t={t}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-(--space-3) border-t border-(--border-subtle) bg-[var(--surface-secondary)] px-(--space-6) py-(--space-4)">
          {saveFailed && (
            <Text as="span" variant="meta" role="alert" className="flex-1 text-[var(--status-warning-text)]">
              {t('dashboard.customize.saveFailed')}
            </Text>
          )}
          <Button
            variant="ghost-text"
            size="sm"
            onClick={onReset}
            data-testid="overview-customize-reset"
          >
            {t('dashboard.customize.reset')}
          </Button>
          <Button
            variant="solid"
            size="sm"
            onClick={onClose}
            data-testid="overview-customize-done"
          >
            {t('dashboard.customize.done')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
